import { Component, OnInit } from '@angular/core';
import { MobileClientDiagnosticService } from 'src/app/services/mobile-client-diagnostic.service';
import { ToastrService } from 'ngx-toastr';

interface AdoptionRow {
  appVersion: string;
  platform: string;
  flavor: string;
  eventCount: number;
  deviceCount: number;
}

@Component({
  selector: 'app-mobile-diagnostics',
  templateUrl: './mobile-diagnostics.component.html',
  styleUrls: ['./mobile-diagnostics.component.scss']
})
export class MobileDiagnosticsComponent implements OnInit {

  events: any[] = [];
  isLoading = false;
  errorMessage = '';

  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  pageSize = 20;

  dateFrom = '';
  dateTo = '';
  filterAppVersion = '';
  filterPlatform = '';
  filterFlavor = '';
  filterEventType = '';
  filterLevel = '';

  showDetail = false;
  selectedEvent: any = null;
  loadingDetail = false;

  showSessionTimeline = false;
  sessionTimeline: any[] = [];
  sessionId = '';
  loadingSession = false;

  adoptionRows: AdoptionRow[] = [];
  adoptionSource: 'api' | 'fallback' | 'none' = 'none';
  adoptionFallbackLimit = 0;
  loadingAdoption = false;

  Math = Math;

  platformOptions = [
    { value: '', labelKey: 'MOBILE_DIAG.FILTER_ALL_PLATFORMS' },
    { value: 'ios', labelKey: 'MOBILE_DIAG.PLATFORM_IOS' },
    { value: 'android', labelKey: 'MOBILE_DIAG.PLATFORM_ANDROID' }
  ];

  levelOptions = [
    { value: '', labelKey: 'MOBILE_DIAG.FILTER_ALL_LEVELS' },
    { value: 'error', labelKey: 'MOBILE_DIAG.LEVEL_ERROR' },
    { value: 'warn', labelKey: 'MOBILE_DIAG.LEVEL_WARN' },
    { value: 'info', labelKey: 'MOBILE_DIAG.LEVEL_INFO' }
  ];

  eventTypeOptions = [
    { value: '', labelKey: 'MOBILE_DIAG.FILTER_ALL_EVENT_TYPES' },
    { value: 'dart_exception', labelKey: 'MOBILE_DIAG.EVENT_DART_EXCEPTION' },
    { value: 'boot_failure', labelKey: 'MOBILE_DIAG.EVENT_BOOT_FAILURE' },
    { value: 'render_error', labelKey: 'MOBILE_DIAG.EVENT_RENDER_ERROR' },
    { value: 'api_failure', labelKey: 'MOBILE_DIAG.EVENT_API_FAILURE' },
    { value: 'operation_slow', labelKey: 'MOBILE_DIAG.EVENT_OPERATION_SLOW' },
    { value: 'operation_hung', labelKey: 'MOBILE_DIAG.EVENT_OPERATION_HUNG' },
    { value: 'provider_error', labelKey: 'MOBILE_DIAG.EVENT_PROVIDER_ERROR' },
    { value: 'session_baseline', labelKey: 'MOBILE_DIAG.EVENT_SESSION_BASELINE' },
    { value: 'heartbeat', labelKey: 'MOBILE_DIAG.EVENT_HEARTBEAT' },
    { value: 'door_open_failure', labelKey: 'MOBILE_DIAG.EVENT_DOOR_OPEN_FAILURE' },
    { value: 'qr_scan_failure', labelKey: 'MOBILE_DIAG.EVENT_QR_SCAN_FAILURE' },
    { value: 'push_registration_failure', labelKey: 'MOBILE_DIAG.EVENT_PUSH_REGISTRATION_FAILURE' },
    { value: 'auth_failure', labelKey: 'MOBILE_DIAG.EVENT_AUTH_FAILURE' },
    { value: 'platform_exception', labelKey: 'MOBILE_DIAG.EVENT_PLATFORM_EXCEPTION' }
  ];

  constructor(
    private diagnosticService: MobileClientDiagnosticService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initDefaultDateRange();
    this.loadEvents();
    this.loadAdoption();
  }

  private initDefaultDateRange(): void {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 14);
    this.dateFrom = this.formatDateInput(from);
    this.dateTo = this.formatDateInput(today);
  }

  private formatDateInput(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private buildFilterParams(extra: Record<string, string | number> = {}): Record<string, string | number> {
    const params: Record<string, string | number> = { ...extra };

    if (this.filterAppVersion.trim()) {
      params['appVersion'] = this.filterAppVersion.trim();
    }
    if (this.filterPlatform) {
      params['platform'] = this.filterPlatform;
    }
    if (this.filterFlavor.trim()) {
      params['flavor'] = this.filterFlavor.trim();
    }
    if (this.filterEventType) {
      params['eventType'] = this.filterEventType;
    }
    if (this.filterLevel) {
      params['level'] = this.filterLevel;
    }
    if (this.dateFrom) {
      params['from'] = this.dateFrom;
    }
    if (this.dateTo) {
      params['to'] = this.dateTo;
    }

    return params;
  }

  loadEvents(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const params = this.buildFilterParams({
      page: this.currentPage,
      limit: this.pageSize
    });

    this.diagnosticService.listDiagnostics(params).subscribe({
      next: (res: any) => {
        this.applyListResponse(res);
        this.isLoading = false;
      },
      error: (error: any) => {
        this.errorMessage = error?.error?.response?.message || 'MOBILE_DIAG.LOAD_ERROR';
        this.toastr.error(error?.error?.response?.message || 'Failed to load mobile diagnostics');
        this.isLoading = false;
      }
    });
  }

  private applyListResponse(res: any): void {
    const data = res?.response?.data ?? res?.data ?? null;
    if (!data) {
      this.events = [];
      return;
    }

    this.events = data.events || [];
    const pagination = data.pagination || {};
    this.totalItems = pagination.total ?? 0;
    this.totalPages = pagination.pages ?? 1;
    this.currentPage = pagination.page ?? this.currentPage;
  }

  loadAdoption(): void {
    this.loadingAdoption = true;
    const params = this.buildFilterParams();
    delete params['page'];
    delete params['limit'];

    this.diagnosticService.getAdoption(params as Record<string, string>).subscribe({
      next: (res: any) => {
        const rows = res?.response?.data ?? res?.data ?? [];
        if (Array.isArray(rows) && rows.length > 0) {
          this.adoptionRows = rows;
          this.adoptionSource = 'api';
        } else {
          this.loadAdoptionFallback();
          return;
        }
        this.loadingAdoption = false;
      },
      error: (error: any) => {
        const status = error?.status;
        if (status === 404) {
          this.loadAdoptionFallback();
        } else {
          this.adoptionRows = [];
          this.adoptionSource = 'none';
          this.loadingAdoption = false;
        }
      }
    });
  }

  private loadAdoptionFallback(): void {
    const fallbackLimit = 100;
    const params = this.buildFilterParams({ page: 1, limit: fallbackLimit });

    this.diagnosticService.listDiagnostics(params).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data ?? {};
        const events = data.events || [];
        this.adoptionRows = this.aggregateAdoptionFromEvents(events);
        this.adoptionSource = events.length > 0 ? 'fallback' : 'none';
        this.adoptionFallbackLimit = fallbackLimit;
        this.loadingAdoption = false;
      },
      error: () => {
        this.adoptionRows = [];
        this.adoptionSource = 'none';
        this.loadingAdoption = false;
      }
    });
  }

  private aggregateAdoptionFromEvents(events: any[]): AdoptionRow[] {
    const cohorts = new Map<string, { eventCount: number; devices: Set<string> }>();

    events.forEach((event) => {
      const appVersion = event.appVersion || '—';
      const platform = event.platform || '—';
      const flavor = event.flavor || '—';
      const key = `${appVersion}|${platform}|${flavor}`;

      if (!cohorts.has(key)) {
        cohorts.set(key, { eventCount: 0, devices: new Set<string>() });
      }

      const cohort = cohorts.get(key)!;
      cohort.eventCount += 1;
      if (event.deviceId) {
        cohort.devices.add(event.deviceId);
      }
    });

    return Array.from(cohorts.entries())
      .map(([key, value]) => {
        const [appVersion, platform, flavor] = key.split('|');
        return {
          appVersion,
          platform,
          flavor,
          eventCount: value.eventCount,
          deviceCount: value.devices.size
        };
      })
      .sort((a, b) => b.eventCount - a.eventCount);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadEvents();
    this.loadAdoption();
  }

  resetFilters(): void {
    this.filterAppVersion = '';
    this.filterPlatform = '';
    this.filterFlavor = '';
    this.filterEventType = '';
    this.filterLevel = '';
    this.initDefaultDateRange();
    this.currentPage = 1;
    this.loadEvents();
    this.loadAdoption();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadEvents();
  }

  viewEventDetail(event: any): void {
    this.selectedEvent = event;
    this.showDetail = true;
    this.showSessionTimeline = false;
    this.loadingDetail = true;

    this.diagnosticService.getDiagnosticDetail(event._id).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data ?? {};
        this.selectedEvent = data.event || data;
        this.loadingDetail = false;
      },
      error: (error: any) => {
        this.loadingDetail = false;
        this.toastr.error(error?.error?.response?.message || 'Failed to load event detail');
      }
    });
  }

  closeDetail(): void {
    this.showDetail = false;
    this.selectedEvent = null;
    this.showSessionTimeline = false;
    this.sessionTimeline = [];
  }

  viewSessionTimeline(): void {
    if (!this.selectedEvent?.diagnosticSessionId) {
      return;
    }

    this.sessionId = this.selectedEvent.diagnosticSessionId;
    this.showSessionTimeline = true;
    this.loadingSession = true;
    this.sessionTimeline = [];

    this.diagnosticService.getSessionTimeline(this.sessionId).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data ?? {};
        this.sessionTimeline = data.events || [];
        this.loadingSession = false;
      },
      error: (error: any) => {
        this.loadingSession = false;
        this.toastr.error(error?.error?.response?.message || 'Failed to load session timeline');
      }
    });
  }

  closeSessionTimeline(): void {
    this.showSessionTimeline = false;
    this.sessionTimeline = [];
  }

  openTimelineEvent(event: any): void {
    this.showSessionTimeline = false;
    this.viewEventDetail(event);
  }

  getLevelBadgeClass(level: string): string {
    switch (level) {
      case 'error': return 'bg-danger';
      case 'warn': return 'bg-warning text-dark';
      case 'info': return 'bg-info text-dark';
      default: return 'bg-secondary';
    }
  }

  truncateMessage(message: string, max = 80): string {
    if (!message) {
      return '—';
    }
    return message.length > max ? `${message.slice(0, max)}…` : message;
  }

  prettyJson(value: any): string {
    if (value == null) {
      return '—';
    }
    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  trackByEventId(index: number, event: any): string {
    return event._id || String(index);
  }
}
